import React, { useState, useEffect } from 'react';
import useFeedback from '../hooks/useFeedback';
import {
  Box,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Typography,
  Grid,
  Alert,
  Chip,
  Tooltip,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Stack,
  Checkbox,
  Divider,
} from '@mui/material';
import { 
  ThumbUp, 
  ThumbDown, 
  CheckCircle, 
  Cancel,
  Refresh,
  FilterList,
  InfoOutlined,
} from '@mui/icons-material';
import {
  getPendingApprovals,
  approveTimeEntry,
  denyTimeEntry,
  getEmployees,
  getShiftSettings,
  getMyManagedEmployees,
} from '../api';
import { useAuth } from '../context/AuthContext';
import ResponsiveTable from './ResponsiveTable';
import WorkflowGuide from './WorkflowGuide';
import { formatTimeDisplay } from '../utils/formatTime';
import { formatPay } from '../utils/formatUtils';

// Helper to format date without timezone conversion
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-');
  return `${month}/${day}/${year}`;
};

// Mirrors DECISION_WHOLE_TENANT_ROLES / decisionScopeIds (server/middleware/auth.js):
// a built-in admin decides for the whole company, everyone else for their direct
// reports only and never for themselves.
const DECIDES_ALL_ROLES = ['admin'];
const QUEUE_SCOPE_TIP = 'Lists the pending entries you can approve: those of your direct reports, never your own. Admins see every pending entry in the company. Having Approve time entries is not enough on its own — the person must be assigned to you on Settings > People.';

export default function ApprovalQueue() {
  const { user } = useAuth();
  const decidesAll = DECIDES_ALL_ROLES.includes(user?.role);
  const [noReports, setNoReports] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [timeFormat, setTimeFormat] = useState('12h');
  const [error, setError] = useState('');
  const [success, showSuccess] = useFeedback(5000);
  const [loading, setLoading] = useState(false);
  
  // Filter state
  const [filterEmployee, setFilterEmployee] = useState('');
  
  // Deny dialog state
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);
  const [denyingEntry, setDenyingEntry] = useState(null);
  const [denyReason, setDenyReason] = useState('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decidesAll, user?.employeeId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [entriesRes, employeesRes, shiftRes, reportsRes] = await Promise.all([
        getPendingApprovals(),
        getEmployees(),
        getShiftSettings().catch(() => ({ data: { time_format: '12h' } })),
        decidesAll ? Promise.resolve(null) : getMyManagedEmployees().catch(() => ({ failed: true })),
      ]);
      // A failed fetch is not "no reports": leave noReports false and show the load
      // error, so the server's answer decides on each action.
      const reportsFailed = !!reportsRes?.failed;
      setNoReports(decidesAll || reportsFailed ? false
        : (reportsRes?.data || []).filter(r => r.employee_id !== user?.employeeId).length === 0);
      setEntries(entriesRes.data);
      setEmployees(employeesRes.data);
      if (shiftRes.data?.time_format) {
        setTimeFormat(shiftRes.data.time_format);
      }
      setError(reportsFailed ? 'Failed to load your direct reports list; decisions are checked by the server.' : '');
    } catch (err) {
      setError('Failed to load pending approvals');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Filter entries by employee
  const filteredEntries = filterEmployee 
    ? entries.filter(e => e.employee_id === parseInt(filterEmployee))
    : entries;

  // Approve a single entry
  const handleApprove = async (entry, notes = '') => {
    try {
      await approveTimeEntry(entry.id, notes);
      showSuccess(`Approved entry for ${entry.first_name} ${entry.last_name}`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve entry');
    }
  };

  // Open deny dialog
  const handleOpenDenyDialog = (entry) => {
    setDenyingEntry(entry);
    setDenyReason('');
    setDenyDialogOpen(true);
  };

  // Close deny dialog
  const handleCloseDenyDialog = () => {
    setDenyDialogOpen(false);
    setDenyingEntry(null);
    setDenyReason('');
  };

  // Submit deny
  const handleDenySubmit = async () => {
    if (!denyReason.trim()) {
      setError('A reason is required when denying an entry');
      return;
    }
    try {
      await denyTimeEntry(denyingEntry.id, denyReason);
      showSuccess(`Entry returned to ${denyingEntry.first_name} ${denyingEntry.last_name} for revision`);
      handleCloseDenyDialog();
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deny entry');
    }
  };

  // Bulk approve
  const handleBulkApprove = async () => {
    if (selectedEntries.length === 0) return;
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const entryId of selectedEntries) {
        try {
          await approveTimeEntry(entryId, '');
          successCount++;
        } catch {
          failCount++;
        }
      }
      
      setSelectedEntries([]);
      loadData();
      
      if (failCount > 0) {
        showSuccess(`Approved ${successCount} entries. ${failCount} failed.`);
      } else {
        showSuccess(`Approved ${successCount} entries`);
      }
    } catch (err) {
      setError('Failed to bulk approve entries');
    }
  };

  // Clear error after timeout
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Define columns for ResponsiveTable
  const columns = [
    {
      field: 'entry_date',
      headerName: 'Date',
      mobilePrimary: true,
      renderCell: ({ row }) => formatDateDisplay(row.entry_date),
    },
    {
      field: 'employee_name',
      headerName: 'Employee',
      mobileSecondary: true,
      renderCell: ({ row }) => `${row.first_name} ${row.last_name}`,
    },
    {
      field: 'company_name',
      headerName: 'Company',
      mobileHidden: true,
      renderCell: ({ row }) => row.company_name || 'N/A',
    },
    {
      field: 'start_time',
      headerName: 'Start',
      mobileHidden: true,
      renderCell: ({ row }) => formatTimeDisplay(row.start_time, timeFormat),
    },
    {
      field: 'end_time',
      headerName: 'End',
      mobileHidden: true,
      renderCell: ({ row }) => formatTimeDisplay(row.end_time, timeFormat),
    },
    {
      field: 'hours_worked',
      headerName: 'Hours',
      align: 'right',
      renderCell: ({ row }) => parseFloat(row.hours_worked).toFixed(2),
    },
    {
      field: 'total_pay',
      headerName: 'Pay',
      align: 'right',
      renderCell: ({ row }) => formatPay(row.total_pay),
    },
    {
      field: 'description',
      headerName: 'Description',
      mobileHidden: true,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {row.description && (
            <Tooltip title={row.description}>
              <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                {row.description}
              </Typography>
            </Tooltip>
          )}
          {row.work_details && (
            <Tooltip title={row.work_details}>
              <Chip size="small" label="Details" variant="outlined" />
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  // Mobile card view
  const renderMobileEntries = () => (
    <Stack spacing={1.5}>
      {filteredEntries.map((entry) => {
        const isSelected = selectedEntries.includes(entry.id);
        return (
          <Card 
            key={entry.id}
            variant="outlined"
            sx={{ 
              border: isSelected ? '2px solid' : '1px solid',
              borderColor: isSelected ? 'primary.main' : 'divider',
            }}
          >
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              {/* Header row */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                <Checkbox
                  checked={isSelected}
                  onChange={() => {
                    setSelectedEntries(prev => 
                      prev.includes(entry.id) 
                        ? prev.filter(e => e !== entry.id) 
                        : [...prev, entry.id]
                    );
                  }}
                  size="small"
                  sx={{ mr: 1, mt: -0.5, ml: -1 }}
                />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {formatDateDisplay(entry.entry_date)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {entry.first_name} {entry.last_name}
                    {entry.company_name && ` - ${entry.company_name}`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton 
                    size="small" 
                    color="success"
                    onClick={() => handleApprove(entry)}
                  >
                    <ThumbUp fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={() => handleOpenDenyDialog(entry)}
                  >
                    <ThumbDown fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* Details row */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Chip
                  size="small"
                  label={`${formatTimeDisplay(entry.start_time, timeFormat)} - ${formatTimeDisplay(entry.end_time, timeFormat)}`}
                  variant="outlined"
                />
                <Chip 
                  size="small" 
                  label={`${parseFloat(entry.hours_worked).toFixed(2)} hrs`}
                  color="primary"
                  variant="outlined"
                />
                <Chip 
                  size="small" 
                  label={formatPay(entry.total_pay)}
                  color="success"
                />
                {entry.work_details && (
                  <Tooltip title={entry.work_details}>
                    <Chip size="small" label="Details" variant="outlined" />
                  </Tooltip>
                )}
              </Box>
              {entry.description && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {entry.description}
                </Typography>
              )}
            </CardContent>
          </Card>
        );
      })}
      {filteredEntries.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            {entries.length === 0 ? 'No entries pending approval' : 'No entries match filter'}
          </Typography>
        </Box>
      )}
    </Stack>
  );

  return (
    <Box sx={{ pb: isMobile ? 8 : 0 }}>
      {/* Header */}
      <Box sx={{ 
        mb: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant={isMobile ? "h6" : "h5"}>
            Approval Queue
            {entries.length > 0 && (
              <Chip
                size="small"
                label={entries.length}
                color="warning"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
          <Tooltip title={QUEUE_SCOPE_TIP}>
            <InfoOutlined fontSize="small" color="action" sx={{ ml: 1 }} />
          </Tooltip>
          <WorkflowGuide showLabel={!isMobile} />
        </Box>
        <Button 
          variant="outlined" 
          startIcon={<Refresh />} 
          onClick={loadData}
          disabled={loading}
          size={isMobile ? "small" : "medium"}
        >
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {!decidesAll && noReports && (
        <Alert severity="info" sx={{ mb: 2 }}>No managed employees assigned — contact an admin. Only your direct reports' entries can be approved here, and never your own.</Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              select
              label="Filter by Employee"
              value={filterEmployee}
              onChange={(e) => setFilterEmployee(e.target.value)}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                startAdornment: <FilterList sx={{ mr: 1, color: 'action.active' }} />,
              }}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">All Employees</MenuItem>
              {employees.map(emp => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.first_name} {emp.last_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={8}>
            <Typography variant="body2" color="text.secondary">
              {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'} pending approval
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Bulk actions */}
      {selectedEntries.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2">
            {selectedEntries.length} selected
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
            onClick={handleBulkApprove}
          >
            Approve All
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={() => setSelectedEntries([])}
          >
            Clear
          </Button>
        </Paper>
      )}

      {/* Entries List/Table */}
      {isMobile ? (
        renderMobileEntries()
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={filteredEntries}
          selectable={true}
          selectedIds={selectedEntries}
          onSelectionChange={setSelectedEntries}
          actions={(row) => (
            <>
              <Tooltip title="Approve">
                <IconButton 
                  size="small" 
                  color="success"
                  onClick={() => handleApprove(row)}
                >
                  <ThumbUp />
                </IconButton>
              </Tooltip>
              <Tooltip title="Return for Revision">
                <IconButton 
                  size="small" 
                  color="error"
                  onClick={() => handleOpenDenyDialog(row)}
                >
                  <ThumbDown />
                </IconButton>
              </Tooltip>
            </>
          )}
          emptyMessage={entries.length === 0 ? "No entries pending approval" : "No entries match filter"}
        />
      )}

      {/* Deny Dialog */}
      <Dialog 
        open={denyDialogOpen} 
        onClose={handleCloseDenyDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Return Entry for Revision</DialogTitle>
        <DialogContent>
          {denyingEntry && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Entry for {denyingEntry.first_name} {denyingEntry.last_name} on {formatDateDisplay(denyingEntry.entry_date)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatTimeDisplay(denyingEntry.start_time, timeFormat)} - {formatTimeDisplay(denyingEntry.end_time, timeFormat)} ({parseFloat(denyingEntry.hours_worked).toFixed(2)} hours)
              </Typography>
            </Box>
          )}
          <Divider sx={{ mb: 2 }} />
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Reason for Return"
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
            placeholder="Please explain why this entry needs to be revised..."
            required
            error={!denyReason.trim()}
            helperText="A reason is required when returning an entry"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDenyDialog}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDenySubmit}
            disabled={!denyReason.trim()}
            startIcon={<Cancel />}
          >
            Return for Revision
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
